import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  X, ShoppingCart, Truck, CheckCircle2, AlertTriangle,
  DollarSign, Package, ArrowRight, Building, Phone
} from 'lucide-react';
import { useReplenishmentSuggestions, useGenerateSuggestedOrders } from '../hooks/useInventory';

interface ReplenishmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ReplenishmentModal: React.FC<ReplenishmentModalProps> = ({ isOpen, onClose }) => {
  const { data, isLoading, refetch } = useReplenishmentSuggestions();
  const generateOrdersMut = useGenerateSuggestedOrders();

  // Estado local para permitir al usuario ajustar las cantidades sugeridas antes de generar
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  useEffect(() => {
    if (data?.all_suggestions) {
      const initial: Record<number, number> = {};
      data.all_suggestions.forEach((s) => {
        initial[s.inventory_item_id] = s.suggested_qty;
      });
      setQuantities(initial);
    }
  }, [data]);

  if (!isOpen) return null;

  const supplierGroups = data?.suppliers_groups || [];
  const totalItems = data?.total_items_to_order || 0;

  // Calcular total dinámico según cantidades editadas
  let totalCost = 0;
  if (data?.all_suggestions) {
    data.all_suggestions.forEach((s) => {
      const q = quantities[s.inventory_item_id] ?? s.suggested_qty;
      totalCost += q * s.unit_cost;
    });
  }

  const handleGenerateOrders = () => {
    if (supplierGroups.length === 0) {
      toast.info('No hay insumos que requieran reposición en este momento.');
      return;
    }

    const ordersPayload = supplierGroups.map((group) => {
      return {
        supplier_id: group.supplier_id,
        items: group.items
          .map((item) => {
            const qty = quantities[item.inventory_item_id] ?? item.suggested_qty;
            return {
              inventory_item_id: item.inventory_item_id,
              quantity: qty,
              unit: item.unit,
              unit_price: item.unit_cost,
            };
          })
          .filter((it) => it.quantity > 0),
      };
    }).filter((ord) => ord.items.length > 0);

    generateOrdersMut.mutate(
      { orders: ordersPayload },
      {
        onSuccess: (res) => {
          toast.success(res.message || '¡Órdenes de compra generadas con éxito!');
          refetch();
          onClose();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Error al generar órdenes de compra.');
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-3xl bg-surface border border-divider shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-divider bg-surface-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-primary/10 text-primary">
              <ShoppingCart size={26} />
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-[color:var(--color-text)]">
                Sugerencia Inteligente de Compras
              </h3>
              <p className="text-xs text-muted mt-0.5">
                Insumos bajo stock mínimo agrupados por proveedor para reposición inmediata en 1 clic.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Resumen Superior */}
        <div className="p-4 bg-surface-muted/20 border-b border-divider flex items-center justify-between flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-muted block text-[10px] uppercase font-bold">Insumos a Reponer</span>
              <strong className="text-sm font-extrabold text-amber-600">{totalItems} insumos</strong>
            </div>
            <div>
              <span className="text-muted block text-[10px] uppercase font-bold">Proveedores a Pedir</span>
              <strong className="text-sm font-extrabold text-primary">{supplierGroups.length} proveedores</strong>
            </div>
          </div>

          <div className="text-right">
            <span className="text-muted block text-[10px] uppercase font-bold">Presupuesto Estimado</span>
            <strong className="text-lg font-extrabold text-emerald-600 font-mono">
              ${Math.round(totalCost).toLocaleString()}
            </strong>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="py-20 text-center text-muted text-sm flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              Calculando niveles de stock y sugerencias de compra...
            </div>
          ) : supplierGroups.length === 0 ? (
            <div className="py-16 text-center text-muted space-y-3">
              <CheckCircle2 size={42} className="mx-auto text-emerald-500" />
              <h4 className="text-base font-bold text-[color:var(--color-text)]">
                ¡Inventario en Niveles Óptimos!
              </h4>
              <p className="text-xs max-w-md mx-auto">
                No hay insumos por debajo del stock mínimo configurado. Tus existencias están al día.
              </p>
            </div>
          ) : (
            supplierGroups.map((group, idx) => (
              <div
                key={group.supplier_id || `unassigned-${idx}`}
                className="rounded-2xl border border-divider overflow-hidden bg-surface shadow-xs"
              >
                {/* Cabecera del proveedor */}
                <div className="p-4 bg-surface-muted/40 border-b border-divider flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-primary/10 text-primary">
                      <Building size={18} />
                    </div>
                    <div>
                      <h4 className="font-extrabold text-sm text-[color:var(--color-text)]">
                        {group.supplier_name}
                      </h4>
                      {group.supplier?.phone && (
                        <p className="text-[11px] text-muted flex items-center gap-1">
                          <Phone size={11} /> {group.supplier.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <span className="text-muted text-[10px] uppercase block">Subtotal</span>
                    <strong className="font-mono text-emerald-600 font-bold">
                      ${Math.round(group.total_cost).toLocaleString()}
                    </strong>
                  </div>
                </div>

                {/* Tabla de insumos */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-surface-muted/20 text-muted uppercase text-[10px] tracking-wider border-b border-divider">
                      <tr>
                        <th className="py-2.5 px-4">Insumo</th>
                        <th className="py-2.5 px-4 text-center">Stock Actual</th>
                        <th className="py-2.5 px-4 text-center">Stock Mínimo</th>
                        <th className="py-2.5 px-4 text-center">Pedir (Sugerido)</th>
                        <th className="py-2.5 px-4 text-right">Costo Unit.</th>
                        <th className="py-2.5 px-4 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-divider/60">
                      {group.items.map((item) => {
                        const currentVal = quantities[item.inventory_item_id] ?? item.suggested_qty;
                        const lineTotal = currentVal * item.unit_cost;
                        return (
                          <tr key={item.inventory_item_id} className="hover:bg-surface-muted/20 transition">
                            <td className="py-3 px-4">
                              <strong className="text-[color:var(--color-text)] block">{item.name}</strong>
                              <span className="text-[10px] font-mono text-muted">{item.sku}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="font-bold text-red-500 font-mono">
                                {item.current_stock} {item.unit}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center text-muted font-mono">
                              {item.min_stock} {item.unit}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="inline-flex items-center gap-1">
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={currentVal}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setQuantities((prev) => ({
                                      ...prev,
                                      [item.inventory_item_id]: val,
                                    }));
                                  }}
                                  className="w-20 px-2 py-1 rounded-lg border border-divider text-center font-mono font-bold text-xs focus:ring-2 focus:ring-primary bg-surface"
                                />
                                <span className="text-muted text-[11px]">{item.unit}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-muted">
                              ${item.unit_cost.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600">
                              ${Math.round(lineTotal).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-divider bg-surface-muted/30 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-divider text-xs font-bold hover:bg-surface-muted transition"
          >
            Cerrar
          </button>

          <button
            onClick={handleGenerateOrders}
            disabled={supplierGroups.length === 0 || generateOrdersMut.isPending}
            className="px-6 py-2.5 rounded-xl bg-primary text-white text-xs font-bold shadow-md hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
          >
            <Truck size={16} />
            {generateOrdersMut.isPending ? 'Generando órdenes...' : 'Generar Órdenes de Compra con 1 Clic'}
          </button>
        </div>
      </div>
    </div>
  );
};
